import { useCallback, useRef, useState } from 'react';

/**
 * Standardized list-loading state utilities.
 *
 * Problem this solves — the "No Data → Data" flicker:
 *   Many pages initialize their loading flag to `false` and only flip it to
 *   `true` inside a post-mount `useEffect`/fetch. On the very first paint
 *   (before the effect runs) the render ladder sees `loading === false` and an
 *   empty data array, so it falls through to the empty state for one frame:
 *
 *     mount → paint: loading=false, data=[] → EMPTY STATE  ← flicker frame
 *       ↓ effect runs
 *     setLoading(true) → SKELETON
 *       ↓ fetch resolves
 *     setLoading(false) + data → DATA
 *
 *   The desired flow is: Loading → Data/Empty (never Empty → Data).
 *
 * The fix is NOT simply initializing the flag to `true`, because many fetches
 * are CONDITIONAL (e.g. only fetch when an account/catalog is selected). A flag
 * hard-coded to `true` would then show a skeleton forever on a branch that will
 * never fetch (e.g. the "select an account" empty state). `willFetch` guards
 * that: the skeleton shows only when a fetch is actually intended.
 */

export interface ListSkeletonInput {
  /** True while a request is actively in flight. */
  loading: boolean;
  /**
   * True once the first load has settled at least once (success OR error).
   * For manual fetches, drive this with {@link useFirstLoad}. For React Query,
   * pass the query's `isFetched`.
   */
  hasLoaded: boolean;
  /**
   * Whether a fetch is actually intended for the current inputs. Defaults to
   * `true` (unconditional fetch on mount). For conditional fetches, pass the
   * SAME guard the fetch effect uses (e.g. `!!selectedAccountId`) so the
   * skeleton never shows on a branch that will never fetch.
   */
  willFetch?: boolean;
}

/**
 * Single source of truth for "should this list render its loading skeleton?".
 *
 * showSkeleton = actively loading, OR a fetch is intended but its first load
 * hasn't settled yet.
 *
 * Truth table (willFetch defaults to true):
 *   loading=true                        → true  (request in flight)
 *   loading=false, willFetch=false       → false (no fetch intended → show empty/branch)
 *   loading=false, willFetch=true,  hasLoaded=false → true  (pre-first-load frame — the flicker window)
 *   loading=false, willFetch=true,  hasLoaded=true  → false (settled → show data/empty)
 */
export function showListSkeleton({ loading, hasLoaded, willFetch = true }: ListSkeletonInput): boolean {
  if (loading) return true;
  if (!willFetch) return false;
  return !hasLoaded;
}

/**
 * Tracks whether a manual (`useState` + `useEffect`) fetch has settled at least
 * once. Call `markLoaded()` in the fetch's `finally` block, then feed
 * `hasLoaded` into {@link showListSkeleton}. `resetLoaded()` is available for
 * manual re-entry into the pre-load state.
 *
 * `resetKey` (optional) — pass the value the fetch is SCOPED to (e.g. the
 * selected business-account id, a channel id). When it changes, `hasLoaded`
 * resets to `false` SYNCHRONOUSLY during render (React's documented
 * "reset-state-when-a-prop-changes" pattern — no effect, no post-paint gap), so
 * the skeleton shows immediately on a scope switch instead of flashing the
 * previous scope's data/empty for a frame before the refetch's `loading` kicks
 * in. Omit it to keep the original one-shot behavior (fully backward-compatible).
 *
 * React Query pages do NOT need this — use the query's `isFetched` (which resets
 * automatically when the query key changes).
 */
export function useFirstLoad(resetKey?: unknown): {
  hasLoaded: boolean;
  markLoaded: () => void;
  resetLoaded: () => void;
} {
  const [hasLoaded, setHasLoaded] = useState(false);
  // Synchronous scope-change reset. Guarded by the ref compare so it runs at
  // most once per distinct key (no render loop); setting state during render
  // makes React re-run this component immediately with hasLoaded=false, before
  // committing — so no stale-scope frame is ever painted.
  const keyRef = useRef(resetKey);
  if (resetKey !== keyRef.current) {
    keyRef.current = resetKey;
    if (hasLoaded) setHasLoaded(false);
  }
  const markLoaded = useCallback(() => setHasLoaded(true), []);
  const resetLoaded = useCallback(() => setHasLoaded(false), []);
  return { hasLoaded, markLoaded, resetLoaded };
}
